import { useState, useRef, useEffect } from "react";
import { BideshaiBrand } from "./BideshaiBrand";
import { useLanguage } from "../i18n/LanguageContext";

// ─── NAV DATA ─────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { key: "nav_how", id: "how-it-works" },
  { key: "nav_visas", id: "visa-categories" },
  { key: "nav_contact", id: "contact" },
];

const languages = [
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "bn", label: "বাং", flag: "🇧🇩" },
  { code: "pt", label: "PT", flag: "🇵🇹" },
];

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

// ─── LANGUAGE DROPDOWN ────────────────────────────────────────────────────────

export function LanguageDropdown() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="lp-lang-dropdown-trigger"
      >
        <span style={{ fontSize: 20, lineHeight: 1 }}>{current.flag}</span>
        <span className="lp-lang-dropdown-label">{current.label}</span>
        <svg
          width="12" height="8" viewBox="0 0 12 8" fill="none"
          style={{ flexShrink: 0, transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <path d="M1 1L6 7L11 1" stroke="#1a2e2a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="lp-lang-dropdown-menu">
          {languages.map(({ code, label, flag }) => (
            <button
              key={code}
              onClick={() => { setLang(code); setOpen(false); }}
              className={`lp-lang-dropdown-item${lang === code ? " active" : ""}`}
            >
              <span style={{ fontSize: 18 }}>{flag}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LANDING NAVBAR ───────────────────────────────────────────────────────────

export function LandingNavbar({ onStart, onGuide, showBackHome = false, onBackHome }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();

  function handleNavClick(link) {
    setMenuOpen(false);
    if (link.isGuide) {
      onGuide?.();
    } else {
      scrollTo(link.id);
    }
  }

  return (
    <>
      <header className="lp-landing-header" style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "#ffffff",
        padding: "0 60px",
        height: 64,
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
        fontFamily: "'Outfit', sans-serif",
      }}>
        {/* Logo */}
        <div className="lp-brand-navbar" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <BideshaiBrand height={36} color="#0B2E28" />
          {showBackHome && (
            <button
              onClick={onBackHome}
              className="lp-guide-back-link"
              style={{
                background: "none",
                border: "none",
                color: "#4A7A70",
                cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                fontSize: 13,
                padding: 0,
              }}
            >
              {t("nav_back")}
            </button>
          )}
        </div>

        {/* Desktop nav */}
        <nav className="lp-desktop-nav">
          {NAV_LINKS.map((link) => (
            <div
              key={link.key}
              className="lp-nav-link"
              onClick={() => handleNavClick(link)}
              style={{
                padding: "8px 10px",
                borderRadius: 12,
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 400,
                color: "#252725",
                whiteSpace: "nowrap",
              }}
            >
              {t(link.key)}
            </div>
          ))}
        </nav>

        {/* Right group: lang switcher + CTA */}
        <div className="lp-desktop-cta" style={{ alignItems: "center", gap: 12 }}>
          <LanguageDropdown />
          <button
            onClick={onGuide}
            className="lp-guide-nav-btn"
            style={{
              alignItems: "center",
              background: "transparent",
              border: "1px solid #D4EDEA",
              borderRadius: 999,
              color: "#0B2E28",
              cursor: "pointer",
              display: "inline-flex",
              fontFamily: "'Outfit', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              gap: 6,
              height: 36,
              padding: "0 16px",
              whiteSpace: "nowrap",
            }}
          >
            {t("nav_guide")} ↗
          </button>
          <button
            onClick={onStart}
            className="lp-navbar-cta"
            style={{
              background: "linear-gradient(180deg, #1abc9d 0%, #3e7179 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: 100,
              padding: "12px 28px",
              height: 48,
              fontSize: 15,
              fontWeight: 400,
              letterSpacing: "0.6px",
              cursor: "pointer",
              fontFamily: "'Outfit', sans-serif",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {t("nav_cta")}
          </button>
        </div>

        {/* Hamburger (mobile) */}
        <button
          className="lp-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span className="lp-hamburger-line" />
          <span className="lp-hamburger-line" />
          <span className="lp-hamburger-line" />
        </button>

        {/* Mobile dropdown */}
        <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
          <div className="lp-mobile-lang-switcher-wrap">
            <LanguageDropdown />
          </div>
          {showBackHome && (
            <div
              className="lp-mobile-link"
              onClick={() => { setMenuOpen(false); onBackHome?.(); }}
            >
              {t("nav_back")}
            </div>
          )}
          <div
            className="lp-mobile-link"
            onClick={() => { setMenuOpen(false); onGuide?.(); }}
          >
            {t("nav_guide")} ↗
          </div>
          {NAV_LINKS.map((link) => (
            <div
              key={link.key}
              className="lp-mobile-link"
              onClick={() => handleNavClick(link)}
            >
              {t(link.key)}
            </div>
          ))}
          <div className="lp-mobile-cta">
            <button
              onClick={() => { setMenuOpen(false); onStart(); }}
              style={{
                background: "#1ABC9C",
                color: "#0B2E28",
                border: "none",
                borderRadius: 999,
                padding: "14px 24px",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                width: "100%",
              }}
            >
              {t("nav_cta")}
            </button>
          </div>
        </div>
      </header>

      {/* Overlay */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: "fixed", inset: 0,
            zIndex: 98,
            background: "rgba(0,0,0,0.2)",
          }}
        />
      )}
    </>
  );
}

// ─── CHECKER NAVBAR ───────────────────────────────────────────────────────────

export function CheckerNavbar({ onBack }) {
  const { t } = useLanguage();
  return (
    <header style={{
      background: "#0B2E28",
      padding: "0 24px",
      height: 56,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontFamily: "'Outfit', sans-serif",
      position: "sticky",
      top: 0,
      zIndex: 100,
    }}>
      {/* Logo — clickable to go home */}
      <div
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: onBack ? "pointer" : "default",
        }}
      >
        <BideshaiBrand height={26} color="#FFFFFF" />
        <span style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.5)",
          background: "rgba(255,255,255,0.1)",
          padding: "2px 8px",
          borderRadius: 999,
          fontWeight: 500,
          letterSpacing: "0.3px",
        }}>
          {t("checker_badge")}
        </span>
      </div>

      {/* Back link */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 13,
            fontWeight: 500,
            color: "#1ABC9C",
            cursor: "pointer",
            fontFamily: "'Outfit', sans-serif",
            textDecoration: "none",
            transition: "text-decoration 0.15s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
        >
          {t("nav_back")}
        </button>
      )}
    </header>
  );
}
